// External Imports
import Papa from "papaparse";
import { useContext, useState } from "react";
import { MdClose } from "react-icons/md";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import { UtilityContext } from "../../context/util-context";
import Heading from "../UI/Heading";
import Modal from "../UI/Modal";
import LinkPair from "../UI/LinkPair";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";

/** ========================================================================
 * * UploadChargingModal Component
 * This component renders a form to upload a csv file containing ev charging.
 * ========================================================================== */

export default function UploadChargingModal({ open, setOpen }) {
  // Load Context
  const { navigate, setWarning } = useContext(UtilityContext);

  // Initialize States
  const [loading, setLoading] = useState(false);
  const [ignored, setIgnored] = useState();

  /* ======================= SUBMIT CSV FILE ======================= */
  const submitHandler = (e) => {
    setLoading(true);
    e.preventDefault();

    try {
      const file = e.target.file.files[0];

      // Read the file content
      const reader = new FileReader();
      reader.onload = ({ target }) => {
        const fileContent = target.result;

        // Try to parse the CSV file using PapaParse
        Papa.parse(fileContent, {
          header: true,
          skipEmptyLines: true,
          complete: async function (results) {
            // Check if there's valid data
            if (results.errors.length > 0 || results.data.length === 0) {
              setWarning(
                "Invalid CSV file! Please upload a properly formatted CSV file."
              );
              setLoading(false);
              return;
            }

            // Process the valid CSV data
            const parsedData = results.data;
            await uploadChargingHandler(parsedData.reverse());
            setLoading(false);
          },

          error: function () {
            // Handle parsing error
            setWarning("Failed to parse CSV. Please check the file format.");
            setLoading(false);
            return;
          },
        });
      };

      // Read the file as text
      reader.readAsText(file);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= UPLOAD CHARGING ======================= */
  const uploadChargingHandler = async (data) => {
    try {
      // Split Data into chunks of 100
      const chunks = [];
      for (let i = 0; i < data.length; i += 100) {
        chunks.push(data.slice(i, i + 100));
      }

      // Initialize Counter
      let notAdded = 0;

      // Upload each chunk
      await Promise.all(
        chunks.map(async (chunk) => {
          const res = await requestAdminAPI("upload-charging", {
            chargings: chunk,
          });
          notAdded += res;
        })
      );

      setIgnored(notAdded);
      setLoading(false);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal lg:w-[30vw] text-sm">
        <Heading
          title="Upload EV Charging"
          text="Download the csv file from the Tesla App (Profile > Account
                    > Charging > History) and upload the file here. Older
                    records and duplicates are automatically ignored."
          isBackArrow={false}
        >
          <MdClose onClick={() => setOpen(false)} className="icon" />
        </Heading>
        <form onSubmit={submitHandler} className="grid gap-6">
          <LinkPair
            label="Upload CSV"
            edit={true}
            inputOptions={{
              required: true,
              type: "file",
              accept: ".csv",
              name: "file",
            }}
          />
          {loading ? (
            <LoadingSpinnerWrapper />
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div onClick={() => setOpen(false)} className="btn-secondary">
                Cancel
              </div>
              <button type="submit" className="btn-primary">
                Submit
              </button>
            </div>
          )}
        </form>
        {ignored && <p className="text-sm text-primary">{ignored} records ignored.</p>}
      </div>
    </Modal>
  );
}
